import {
  ApiLineItemTaxComponent,
  ApiPriceBook,
  ApiPriceBookProduct,
  ApiTaxRate,
} from '@vend/types'
import {
  add,
  divide,
  isFinite,
  multiply,
  round,
  subtract,
  vn,
} from 'vend-number'
import omit from 'lodash/omit'
import keyBy from 'lodash/keyBy'
import { PromotionLineItem } from '../components/sale/sale-promotion/sale-promotion-resource'
import { PromotionDetails } from '../models/promotion'
import {
  LineItem,
  LineItemTaxComponent,
  LineItemTotals,
} from '../models/line-item'
import { FulfilmentType } from '../models/fulfilment'
import { getTaxCode } from '../functions/lineItem'
import { calculateTax, calculateTaxComponentUnitAmount } from './calculate-tax'
import {
  calculateTaxAppropriatePrice,
  calculateUnroundedDisplayDiscountInfo,
} from './calculate-price'

export const PRICE_BOOK_TYPE_BASE = 'BASE'

export interface BasicPriceInfo {
  price: string | number
  tax: string | number
  loyaltyValue: string | number
  explicitLoyaltyValue: boolean
}

interface PriceInfo extends BasicPriceInfo {
  priceBook?: ApiPriceBook
  lineItemPromotion?: PromotionLineItem
  promotionDetails?: PromotionDetails[]
}

export interface LineItemPricing extends PriceInfo {
  basePrice: string | number
  baseTax: string | number
  bestPrice: string | number // best price from pricebook, used to calculate the best pricebook discount percent
  bestTax: string | number // best tax from pricebook,  used to calculate the best pricebook discount percent
}

interface PriceBookTuple {
  priceBook?: ApiPriceBook
  priceBookProduct: ApiPriceBookProduct
}

interface BestPriceBookInfo {
  basePriceInfo?: PriceInfo
  bestPriceInfo?: PriceInfo
}

export interface PromotionOptions {
  removePromotion?: boolean
  lineItemPromotion?: PromotionLineItem
  promotionDetails?: PromotionDetails[]
}

interface MinLineItem {
  adjusted?: boolean
  price: number | string
  tax: number | string
  loyaltyValue: number | string
  explicitLoyaltyValue: boolean
  product: { id: string }
  lineItemPromotion?: PromotionLineItem | null
  promotionDetails?: PromotionDetails[] | null
}

export type LineItemDisplayPricing = {
  displayBasePrice: string | number
  displayPrice: string | number
  displayDiscount: string | number
  displayDiscountPercent: string | number
  displayMarkupPercent: string | number
  displayBestDiscountPercent: string | number
  displayBestDiscountPrice: string | number
}

export interface LineItemTotalsAndTaxComponents {
  totals: LineItemTotals
  taxComponents: LineItemTaxComponent[]
}

/**
 * Calculate the pricing for the given line item.
 *
 * @param lineItem The line item to price
 * @param priceBookTuples The relevant price books and price book products for the given line item
 * @param taxRate The relevant tax rate for the given line item
 * @param promotionOptions Any line item promotions to apply to the given line item
 * @param isTaxInclusive Whether the current store is tax inclusive or tax exclusive
 * @param loyaltyEnabled Whether loyalty earning is enabled for the current store
 * @param loyaltyRatio The ratio that loyalty is earned at for the current store
 *
 * @returns {LineItemPricing} The basePrice, baseTax, price, tax, loyaltyValue, explicitLoyaltyValue and optionally
 *          priceBook, lineItemPromotion and promotionDetails calculated for the given line item
 */
export function calculateLineItemPricing(
  lineItem: Readonly<MinLineItem>,
  priceBookTuples: Readonly<PriceBookTuple[]>,
  taxRate: string,
  promotionOptions: Readonly<PromotionOptions>,
  isTaxInclusive: boolean,
  loyaltyEnabled: boolean,
  loyaltyRatio: number
): LineItemPricing {
  const { basePriceInfo, bestPriceInfo } = calculateBestPriceBookInfo(
    priceBookTuples,
    taxRate,
    isTaxInclusive,
    loyaltyEnabled,
    loyaltyRatio
  )

  if (!basePriceInfo || !isFinite(basePriceInfo.price)) {
    throw new Error(
      `No base price book found for line item. ProductId: ${lineItem.product.id}, BasePrice: ${basePriceInfo?.price}`
    )
  }

  const basePrice = basePriceInfo.price
  const baseTax = basePriceInfo.tax
  const bestPrice = bestPriceInfo?.price || basePrice
  const bestTax = bestPriceInfo?.tax || baseTax

  if (lineItem.adjusted) {
    let { explicitLoyaltyValue } = lineItem
    if (explicitLoyaltyValue) {
      // Since we're updating the price, re-evaluate `explicitLoyaltyValue`.  If there is an explicit loyalty value
      // set in the base price book or promotion, then we should not be recalculating explicitly set loyalty
      // for this line item.
      explicitLoyaltyValue =
        basePriceInfo.explicitLoyaltyValue ||
        Boolean(bestPriceInfo?.explicitLoyaltyValue)
    }

    return {
      basePrice,
      baseTax,
      bestPrice,
      bestTax,
      ...calculateAdjustedPriceInfo(
        lineItem.price,
        taxRate,
        lineItem.loyaltyValue,
        explicitLoyaltyValue,
        isTaxInclusive,
        loyaltyEnabled,
        loyaltyRatio
      ),
    }
  }

  const promotionPriceInfo = calculatePromotionPriceInfo(
    lineItem.lineItemPromotion || null,
    lineItem.promotionDetails || null,
    promotionOptions,
    loyaltyEnabled
  )

  return {
    basePrice,
    baseTax,
    bestPrice,
    bestTax,
    // Apply pricing in order of preference: promotion > non-base PriceBook > base PriceBook
    ...(promotionPriceInfo ||
      bestPriceInfo ||
      // We only want to include the relevant PriceBook if it was not the base PriceBook
      omit(basePriceInfo, 'priceBook')),
  }
}

/**
 * Calculate the pricing for a gift card line item based on the given base price and tax rate.
 *
 * @param basePrice The base price of the gift card line item (the amount on the gift card)
 * @param taxRate The relevant tax rate for the given line item
 *
 * @returns {LineItemPricing} The basePrice, baseTax, price, tax, loyaltyValue, explicitLoyaltyValue and optionally
 *          priceBook, lineItemPromotion and promotionDetails calculated for the given line item
 */
export function calculateGiftCardLineItemPricing(
  basePrice: string,
  taxRate: string
): LineItemPricing {
  const price = round(basePrice, 5)
  const baseTax = calculateTax(basePrice, taxRate)

  return {
    basePrice: price,
    baseTax,
    price,
    tax: baseTax,
    bestPrice: price,
    bestTax: baseTax,
    // Explicit loyalty value of 0 so that we don't attempt to recalculate it - loyalty is not earned on a gift
    // card but rather earned on the products purchased with it when it is redeemed.
    loyaltyValue: '0',
    explicitLoyaltyValue: true,
  }
}

/**
 * Calculate the display pricing based on the given base and sale prices and taxes as appropriate for a tax inclusive
 * or tax exclusive merchant.
 *
 * @param basePrice The base price of the line item
 * @param baseTax The base tax of the line item
 * @param price The sale price of the line item
 * @param tax The sale tax of the line item
 * @param bestPrice The best pricebook price of the line item
 * @param bestTax The best pricebook tax of the line item
 * @param isTaxInclusive Whether the current store is tax inclusive or tax exclusive
 *
 * @returns {LineItemDisplayPricing} The displayBasePrice, displayPrice, displayDiscount, displayDiscountPercent and
 *          displayMarkupPercent calculated based on the given prices and taxes
 */
export function calculateLineItemDisplayPricing(
  basePrice: string | number,
  baseTax: string | number,
  price: string | number,
  tax: string | number,
  bestPrice: string | number,
  bestTax: string | number,
  isTaxInclusive: boolean
): LineItemDisplayPricing {
  const displayPrice = round(
    calculateTaxAppropriatePrice(price, tax, isTaxInclusive),
    5
  )

  const {
    discount,
    discountPercent,
    markupPercent,
  } = calculateUnroundedDisplayDiscountInfo(
    basePrice,
    baseTax,
    price,
    tax,
    isTaxInclusive
  )

  const {
    discountPercent: displayBestDiscountPercent,
  } = calculateUnroundedDisplayDiscountInfo(
    basePrice,
    baseTax,
    bestPrice,
    bestTax,
    isTaxInclusive
  )

  return {
    displayBasePrice: round(
      calculateTaxAppropriatePrice(basePrice, baseTax, isTaxInclusive),
      5
    ),
    displayBestDiscountPrice: round(
      calculateTaxAppropriatePrice(bestPrice, bestTax, isTaxInclusive),
      5
    ),
    displayPrice,
    displayDiscount: round(discount, 5),
    displayDiscountPercent: discountPercent,
    displayMarkupPercent: markupPercent,
    displayBestDiscountPercent,
  }
}

export interface BaseLineItemTotalsCalculationParams {
  discountPercentAsDecimal: string | number
  returnDiscountPercentAsDecimal: string | number | null
  saleIsConfirmed: boolean
  saleIsSavedUnfulfilled?: boolean
  isTaxInclusive: boolean
  isAutoTaxActive?: boolean
}

interface LineItemTotalsCalculationParams
  extends BaseLineItemTotalsCalculationParams {
  lineItem: Readonly<LineItem>
}

/**
 * Calculates the totals object for a line item and also returns tax components.
 */
export function calculateLineItemTotals({
  lineItem,
  discountPercentAsDecimal,
  returnDiscountPercentAsDecimal,
  saleIsConfirmed,
  saleIsSavedUnfulfilled,
  isTaxInclusive,
  isAutoTaxActive,
}: LineItemTotalsCalculationParams): LineItemTotalsAndTaxComponents {
  const { quantity, price, basePrice } = lineItem

  // set exclusive price
  const exclusivePrice = multiply(price, quantity)
  const exclusiveBasePrice = multiply(basePrice, quantity)

  // default saleDiscount to 0
  let saleDiscount = 0

  // If we have a return discount percentage
  if (returnDiscountPercentAsDecimal) {
    if (lineItem.isReturn) {
      // Update sale discount using the return discount percentage
      saleDiscount = multiply(returnDiscountPercentAsDecimal, exclusivePrice)
    }
    // Otherwise use the current discount percentage
  } else if (vn(discountPercentAsDecimal).gt(0)) {
    saleDiscount = multiply(discountPercentAsDecimal, exclusivePrice)

    // check if lineItem is part of layby/on account/saved unfulfilled sale
    const lineItemShouldNotApplyDiscount =
      (saleIsConfirmed || saleIsSavedUnfulfilled) && !lineItem.confirmed

    if (lineItemShouldNotApplyDiscount) {
      // don't apply sale discount if we're in a return/confirmed sale
      // and the lineItem is not part of that return/confirmed sale
      saleDiscount = 0
    }
  }

  const useAutoTaxValues =
    isAutoTaxActive &&
    lineItem.fulfilmentType === FulfilmentType.DELIVERY &&
    !lineItem.customerTaxApplied

  const taxComponents = calculateLineItemTaxComponents(
    lineItem,
    saleDiscount,
    useAutoTaxValues
  )

  const saleDiscountTax = taxComponents.reduce(
    (sdt, tc) =>
      add(sdt, subtract(tc.total_tax, tc.total_tax_after_sale_discount)),
    0
  )

  const tax = taxComponents.reduce((s, x) => {
    return add(s, x.total_tax)
  }, 0)
  const baseTax = taxComponents.reduce((s, x) => add(s, x.base_total_tax), 0)

  const inclusivePrice = add(exclusivePrice, tax)
  const inclusiveBasePrice = add(exclusiveBasePrice, baseTax)

  // Display the non-adjusted line item price
  const displayPrice = isTaxInclusive ? inclusivePrice : exclusivePrice
  const displayBasePrice = isTaxInclusive
    ? inclusiveBasePrice
    : exclusiveBasePrice

  const totals: LineItemTotals = {
    exclusivePrice,
    exclusiveBasePrice,
    saleDiscount,
    saleDiscountTax,
    tax,
    baseTax,
    inclusivePrice,
    inclusiveBasePrice,
    displayPrice,
    displayBasePrice,
  }

  return { totals, taxComponents }
}

function calculateBestPriceBookInfo(
  priceBookTuples: Readonly<PriceBookTuple[]>,
  taxRate: string,
  isTaxInclusive: boolean,
  loyaltyEnabled: boolean,
  loyaltyRatio: number
): BestPriceBookInfo {
  let basePriceInfo: PriceInfo | undefined
  let bestPriceInfo: PriceInfo | undefined

  priceBookTuples.forEach(({ priceBook, priceBookProduct }) => {
    const priceInfo = calculatePriceBookPriceInfo(
      priceBook,
      priceBookProduct.price,
      priceBookProduct.loyalty_value,
      taxRate,
      isTaxInclusive,
      loyaltyEnabled,
      loyaltyRatio
    )

    if (priceBook?.type === PRICE_BOOK_TYPE_BASE) {
      basePriceInfo = priceInfo
      return
    }

    if (!isFinite(priceInfo.price)) {
      return
    }

    if (!bestPriceInfo) {
      bestPriceInfo = priceInfo
      return
    }

    const bestPrice = bestPriceInfo.price
    const newPrice = vn(priceInfo.price)
    if (
      newPrice.lt(bestPrice) ||
      (newPrice.eq(bestPrice) &&
        vn(priceInfo.loyaltyValue).gt(bestPriceInfo.loyaltyValue))
    ) {
      // The best price info is the non-base pricebook with the lowest price or highest loyalty value (if prices are
      // equal)
      bestPriceInfo = priceInfo
    }
  })

  return {
    basePriceInfo,
    bestPriceInfo,
  }
}

function calculateAdjustedPriceInfo(
  adjustedPrice: number | string,
  taxRate: string,
  loyaltyValue: number | string,
  explicitLoyaltyValue: boolean,
  isTaxInclusive: boolean,
  loyaltyEnabled: boolean,
  loyaltyRatio: number | string
): PriceInfo {
  const price = round(adjustedPrice, 5)
  const tax = calculateTax(price, taxRate)

  let loyaltyVal = '0'
  if (loyaltyEnabled) {
    loyaltyVal = explicitLoyaltyValue
      ? round(loyaltyValue, 5)
      : calculateLoyaltyValue(price, tax, loyaltyRatio, isTaxInclusive)
  }

  return {
    price,
    tax,
    loyaltyValue: loyaltyVal,
    explicitLoyaltyValue,
  }
}

function calculatePriceBookPriceInfo(
  priceBook: Readonly<ApiPriceBook | undefined>,
  priceBookPrice: number | string,
  priceBookLoyaltyValue: number | string | null,
  taxRate: string,
  isTaxInclusive: boolean,
  loyaltyEnabled: boolean,
  loyaltyRatio: number | string
): PriceInfo {
  const price = round(priceBookPrice, 5)
  const tax = calculateTax(price, taxRate)

  let loyaltyValue = '0'
  let explicitLoyaltyValue = false

  if (loyaltyEnabled) {
    if (isFinite(priceBookLoyaltyValue)) {
      loyaltyValue = round(priceBookLoyaltyValue!, 5)
      explicitLoyaltyValue = true
    } else {
      loyaltyValue = calculateLoyaltyValue(
        price,
        tax,
        loyaltyRatio,
        isTaxInclusive
      )
    }
  }

  return {
    price,
    tax,
    loyaltyValue,
    explicitLoyaltyValue,
    priceBook,
  }
}

function calculateLoyaltyValue(
  price: number | string,
  tax: number | string,
  loyaltyRatio: number | string,
  isTaxInclusive: boolean
): string {
  const loyaltyPrice = isTaxInclusive ? add(price, tax) : price
  return round(multiply(loyaltyPrice, loyaltyRatio), 5)
}

function calculatePromotionPriceInfo(
  existingLineItemPromotion: Readonly<PromotionLineItem> | null,
  existingPromotionDetails: PromotionDetails[] | null,
  promotionOptions: Readonly<PromotionOptions>,
  loyaltyEnabled: boolean
): PriceInfo | null {
  if (promotionOptions.removePromotion) {
    return null
  }

  let { lineItemPromotion, promotionDetails } = promotionOptions

  if (!lineItemPromotion) {
    if (existingLineItemPromotion) {
      lineItemPromotion = existingLineItemPromotion
      promotionDetails = existingPromotionDetails || undefined
    } else {
      return null
    }
  }

  return {
    price: round(
      add(lineItemPromotion.unit_price, lineItemPromotion.unit_discount),
      5
    ),
    tax: round(
      add(lineItemPromotion.unit_tax, lineItemPromotion.unit_tax_discount),
      5
    ),
    loyaltyValue: loyaltyEnabled
      ? round(lineItemPromotion.loyalty_value, 5)
      : '0',
    explicitLoyaltyValue:
      loyaltyEnabled && lineItemPromotion.explicit_loyalty_value,
    lineItemPromotion,
    promotionDetails,
  }
}

/**
 * Update tax components for a line item based on price, quantity and tax rates
 * Must not rely on totals being up to date.
 *
 * @param lineItem
 */
function calculateLineItemTaxComponents(
  lineItem: Readonly<LineItem>,
  saleDiscount: string | number,
  useAutoTaxValues: boolean = false
): LineItemTaxComponent[] {
  const { quantity, price: unitPrice, basePrice: unitBasePrice } = lineItem
  const rates = lineItem.taxInfo?.rates || []

  if (!rates.length) {
    return []
  }

  const taxCode = getTaxCode(lineItem)

  const unitSaleDiscount = vn(quantity).eq(0)
    ? '0'
    : divide(saleDiscount, quantity)
  const unitPriceAfterDiscount = subtract(unitPrice, unitSaleDiscount)

  return rates.map(rate => {
    // When auto-tax is active, use the server-calculated values if available
    // for this rate. Server values account for tax codes and thresholds that
    // local calculation cannot replicate. When taxInfo has been changed
    // (e.g. tax removed), the rate IDs won't match and we fall through to
    // local calculation.
    const existingComponent = useAutoTaxValues
      ? lineItem.taxComponents?.find(tc => tc.rate_id === rate.id)
      : undefined

    if (existingComponent) {
      return existingComponent
    }

    return {
      rate_id: rate.id,
      rate,
      total_tax: multiply(
        quantity,
        calculateTaxComponentUnitAmount(rate, unitPrice, taxCode)
      ),
      total_tax_after_sale_discount: multiply(
        quantity,
        // TODO: Should we be passing the full unitPrice in here as well and using that to evaluate tax thresholds?
        calculateTaxComponentUnitAmount(rate, unitPriceAfterDiscount, taxCode)
      ),
      base_total_tax: multiply(
        quantity,
        calculateTaxComponentUnitAmount(rate, unitBasePrice, taxCode)
      ),
    }
  })
}

type MinTaxRate = Pick<ApiTaxRate, 'id' | 'rate' | 'rules'>

interface CalculateCustomTaxRatesParams<T extends MinTaxRate> {
  rates: readonly T[]
  taxComponents?: ApiLineItemTaxComponent[]
  totalExclusivePrice: string | number
  isReturn: boolean
  isConfirmed: boolean
}

/**
 * Detect any custom tax rates (where a different rate is used to the rate associated with the tax ID) based on the
 * given tax rates, tax components and total exclusive price. This only applies if the relevant line item is a return
 * or confirmed line item.
 *
 * If we detect any custom tax rates we return a new collection of ApiTaxRates updated with the custom rates, otherwise
 * return null indicating the given tax rates are correct for the given tax components.
 *
 * See https://vendhq.atlassian.net/browse/LR-8488 for more details.
 */
export function calculateCustomTaxRates<T extends MinTaxRate>({
  totalExclusivePrice,
  rates,
  taxComponents,
  isReturn,
  isConfirmed,
}: CalculateCustomTaxRatesParams<T>): T[] | null {
  if (!isReturn && !isConfirmed) {
    // We only need to calculate custom tax rates for returns or confirmed sales
    return null
  }

  if (
    !rates.length ||
    !taxComponents?.length ||
    vn(totalExclusivePrice).eq(0)
  ) {
    return null
  }

  const ratesById = keyBy(rates, 'id')

  let customRateDetected = false
  let updatedRates: T[]

  try {
    updatedRates = taxComponents.map(taxComponent => {
      const taxRate = ratesById[taxComponent.rate_id]
      if (!taxRate) {
        throw new Error('Tax component references missing tax rate')
      }

      const expectedTotal = calculateExpectedTax(
        taxRate,
        totalExclusivePrice,
        isReturn
      )
      let totalTax = taxComponent.total_tax

      // This is to handle a bug where tax components weren't negated when a return sale is created. The bug has since
      // been fixed (https://github.com/vend/vend/pull/18946) however because the created tax components are persisted
      // we need to continue to handle this situation for some time to handle historically created return sales.
      if (isReturn && vn(totalTax).div(totalExclusivePrice).lt(0)) {
        totalTax = multiply(totalTax, -1)
      }

      if (expectedTotal !== round(totalTax, 5)) {
        customRateDetected = true

        // In the case of a threshold tax (on the over threshold value) we could calculate
        // the rate over the threshold here as well, but that would rely on any custom tax components
        // to be based off of the threshold. Safer to just calculate over the total price
        const updatedRate = Number(
          round(divide(totalTax, totalExclusivePrice), 4)
        )
        return { ...taxRate, rate: updatedRate }
      }

      return taxRate
    })
  } catch (_) {
    // An error here means the IDs of the tax rates in the `rates` collection doesn't match those in the `taxComponents`
    // collection. In this situation we just return `null` and allow the sale to be calculated from the base rates as
    // the tax components are likely invalid.
    return null
  }

  if (customRateDetected) {
    return updatedRates
  }

  return null
}

function calculateExpectedTax(
  taxRate: MinTaxRate,
  totalExclusivePrice: string | number,
  isReturn: boolean
) {
  const taxCode = taxRate.rules?.[0]?.tax_code

  if (isReturn) {
    // Negate calculated tax for returns so they're negative
    return round(
      // Convert totalExclusivePrice to positive for accurate tax calculation and threshold handling
      -calculateTaxComponentUnitAmount(taxRate, -totalExclusivePrice, taxCode),
      5
    )
  }

  return round(
    calculateTaxComponentUnitAmount(taxRate, totalExclusivePrice, taxCode),
    5
  )
}

/**
 * @private
 *
 * Exported only to allow testing private functions.
 */
export const testables = {
  calculateLineItemTaxComponents,
}
